/**
 * Axios adapter for WLT1 transit encryption.
 *
 * Encrypts request bodies and decrypts response bodies for every gateway-
 * routed service in the admin panel; the single `REACT_APP_WLT_ENABLED`
 * flag turns the whole layer on or off.
 *
 * The admin panel has one shared axios instance whose baseURL is the
 * gateway origin. The gateway strips a service prefix (`/user_service`,
 * `/payment_service`, …) in integration, so the server never sees it —
 * which means the client has to strip the same prefix before computing
 * the AAD. `SERVICE_PREFIXES` lists every service we currently route
 * through; `matchServicePrefix` finds the one for a given request. A
 * request whose path doesn't match any known service is passed through
 * plaintext (safer than guessing the wrong prefix and corrupting the AAD).
 *
 * Skips FormData / multipart bodies (file uploads). GET / bodyless requests
 * still get the four WLT headers but no `{d:…}` body.
 *
 * Register with `attachWltRequest(instance)` *after* the auth interceptor —
 * axios runs request interceptors LIFO, so WLT then seals the body first and
 * the auth interceptor adds `Authorization` on top.
 */

import {
    looksEnveloped,
    newMessageId,
    normalisePath,
    nowSeconds,
    requestAad,
    responseAad,
    seal,
    unseal,
    unwrapBody,
    utf8Encode,
    utf8Decode,
} from './wlt1'
import { getKeys, wltEnabled, wltKid } from './wltConfig'

// Every gateway-routed service in the admin panel. If backend adds a new
// one, append its prefix here (leading slash, no trailing slash).
const SERVICE_PREFIXES = [
    '/user_service',
    '/payment_service',
    '/market_data_service',
    '/portfolio_service',
    '/analytics_service',
    '/bse_star_mf_service',
]

function matchServicePrefix(config) {
    // baseURL = gateway origin only; url may or may not have a leading slash.
    const abs = new URL(config.url ?? '', config.baseURL)
    const pathname = abs.pathname
    for (const prefix of SERVICE_PREFIXES) {
        if (pathname === prefix || pathname.startsWith(prefix + '/')) {
            return { prefix, abs }
        }
    }
    return null
}

function isMultipart(config) {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) return true
    const ct =
        config.headers?.['Content-Type'] ||
        config.headers?.['content-type'] ||
        config.headers?.common?.['Content-Type'] ||
        ''
    return typeof ct === 'string' && ct.toLowerCase().includes('multipart/form-data')
}

function toPlaintextBytes(data) {
    // ApiService already JSON.stringify's POST/PATCH/PUT bodies → typeof === 'string'.
    // DELETE may pass a raw object.
    if (data === null || data === undefined) return null
    if (typeof data === 'string') return utf8Encode(data)
    return utf8Encode(JSON.stringify(data))
}

function stashContext(config, method, path, msgId) {
    config.__wlt = { kid: wltKid, method, path, msgId }
}

/**
 * Attach the WLT request interceptor. Idempotent — call once at instance
 * setup time.
 */
export function attachWltRequest(instance) {
    if (instance.__wltAttached) return
    instance.__wltAttached = true

    instance.interceptors.request.use(async config => {
        if (!wltEnabled) return config
        let match
        try {
            match = matchServicePrefix(config)
        } catch {
            return config // if URL parsing fails, don't block the request
        }
        if (!match) return config
        if (isMultipart(config)) return config

        const method = (config.method ?? 'get').toUpperCase()
        const path = normalisePath(match.abs.href, match.prefix)
        const ts = nowSeconds()
        const msgId = newMessageId()
        const keys = await getKeys()

        stashContext(config, method, path, msgId)
        config.headers = config.headers || {}
        config.headers['X-Weallat-Client'] = 'web'
        config.headers['X-Weallat-Key-Id'] = wltKid
        config.headers['X-Weallat-Ts'] = String(ts)
        config.headers['X-Weallat-Msg-Id'] = msgId

        const plaintext = toPlaintextBytes(config.data)
        if (plaintext !== null) {
            const envelope = await seal(
                keys.requestKey,
                wltKid,
                plaintext,
                requestAad(wltKid, method, path, ts, msgId),
            )
            config.data = JSON.stringify({ d: envelope })
            config.headers['Content-Type'] = 'application/json'
        }
        return config
    })
}

function getResponseHeader(response, name) {
    // axios lowercases response header keys
    const h = response?.headers ?? {}
    return h[name] ?? h[name.toLowerCase()] ?? ''
}

async function decryptResponsePayload(response) {
    const ctx = response.config?.__wlt
    if (!ctx) return response
    if (!response.data || typeof response.data !== 'object') return response

    // Legitimate cleartext: policy `off` or exempt path.
    if (!looksEnveloped(response.data)) return response

    const keys = await getKeys()
    const envelope = unwrapBody(response.data)
    const opened = await unseal(
        keys.responseKey,
        envelope,
        responseAad(
            ctx.kid,
            ctx.method,
            ctx.path,
            getResponseHeader(response, 'x-weallat-ts'),
            getResponseHeader(response, 'x-weallat-msg-id'),
            ctx.msgId,
        ),
    )
    const text = utf8Decode(opened)
    // Some routes may return an empty body even under encryption.
    response.data = text ? JSON.parse(text) : {}
    return response
}

/**
 * Response-side helper for the success branch. Returns the response with
 * `response.data` decrypted in place (or unchanged if plaintext).
 */
export async function wltDecryptResponse(response) {
    if (!wltEnabled) return response
    try {
        return await decryptResponsePayload(response)
    } catch (err) {
        // Attach the transport error code so upstream handling can react.
        response.__wltDecryptError = err
        throw err
    }
}

/**
 * Error-side helper. The server encrypts 4xx bodies too — decrypt before
 * `apiError` toasts, or the toast shows ciphertext.
 */
export async function wltDecryptError(error) {
    if (!wltEnabled || !error?.response) return error
    try {
        await decryptResponsePayload(error.response)
    } catch {
        // If decryption itself fails, leave the response body as-is; apiError
        // will still surface *something*, and the failure code lives in the
        // caught error.
    }
    return error
}

/**
 * Optional: react to transport error codes surfaced by the server as
 * cleartext (`response.data.error.code` starts with `transport_`).
 * Callers can pass a handler that force-logs-out on unknown-kid, retries
 * once on clock-skew, etc. Currently unused — hook up if/when needed.
 */
export function onTransportError(response, handler) {
    const code = response?.data?.error?.code
    if (typeof code === 'string' && code.startsWith('transport_') && handler) {
        handler(code, response)
    }
}
